import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { categoriesApi, productsApi, Category, Item } from '../../services/api';
import { cartService } from '../../services/cartService';
import OverflowTooltip from '../common/OverflowTooltip';
import { getFullProductTitle } from '../../utils/productText';
import './Categories.css';

const Categories: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [allItems, setAllItems] = useState<Item[]>([]);
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [modalImageIndex, setModalImageIndex] = useState(0);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await categoriesApi.getAll();
            setCategories(response.data.categories);
        } catch (err) {
            setError('Failed to fetch categories');
            console.error('Error fetching categories:', err);
        } finally {
            setLoading(false);
        }
    };

    // Sort items by createdAt descending (newest first)
    const sortItemsByCreatedAt = useCallback((items: Item[]): Item[] => {
        return [...items].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;

            if (dateA > 0 && dateB > 0) return dateB - dateA;
            if (dateA > 0 && dateB === 0) return -1;
            if (dateA === 0 && dateB > 0) return 1;
            return 0;
        });
    }, []);

    const fetchAllItems = useCallback(async () => {
        try {
            const response = await productsApi.getAll();
            const items = response.data.products || response.data.items || [];
            setAllItems(sortItemsByCreatedAt(items));
        } catch (err) {
            console.error('Error fetching all items:', err);
        }
    }, [sortItemsByCreatedAt]);

    useEffect(() => {
        if (categories.length > 0) {
            fetchAllItems();
        }
    }, [categories, fetchAllItems]);

    // Filter items based on search query
    const getFilteredItems = (items: Item[]): Item[] => {
        let filtered = items;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = items.filter(item =>
                item.id.toLowerCase().includes(query) ||
                item.name.toLowerCase().includes(query)
            );
        }
        // Ensure filtered items are also sorted
        return sortItemsByCreatedAt(filtered);
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    return (
        <section id="categories" className="categories" aria-labelledby="categories-heading">
            <h2 id="categories-heading" className="section-heading section-heading--center categories-heading">
                <span>Textiles as beautiful</span>
                <span>as they are timeless</span>
            </h2>

            {/* Search Bar */}
            <div className="search-container">
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search by Item ID or Title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                    <button
                        className="search-clear"
                        onClick={() => setSearchQuery('')}
                        title="Clear search"
                    >
                        ×
                    </button>
                )}
            </div>

            {searchQuery && (
                <div className="search-results">
                    <h3 className="search-results-title">
                        Search Results ({getFilteredItems(allItems).length} found)
                    </h3>
                    {getFilteredItems(allItems).length > 0 ? (
                        <div className="items-scroll search-items-scroll">
                            {getFilteredItems(allItems).map((item) => (
                                <ItemCard key={item.id} item={item} onItemClick={setSelectedItem} />
                            ))}
                        </div>
                    ) : (
                        <div className="no-search-results">
                            <p>No items found matching "{searchQuery}"</p>
                            <p className="search-hint">Try searching by Item ID or Item Title</p>
                        </div>
                    )}
                </div>
            )}

            {!searchQuery && (
                <div className="categories-list">
                    {categories.map((category) => (
                        <CategoryCard
                            key={category.id}
                            category={category}
                            searchQuery={searchQuery}
                            getFilteredItems={getFilteredItems}
                            onItemClick={setSelectedItem}
                        />
                    ))}
                </div>
            )}

            {/* Item Detail Modal */}
            {selectedItem && (
                <ItemDetailModal
                    item={selectedItem}
                    onClose={() => {
                        setSelectedItem(null);
                        setModalImageIndex(0);
                    }}
                    imageIndex={modalImageIndex}
                    onImageIndexChange={setModalImageIndex}
                    />
            )}
        </section>
    );
};

interface CategoryCardProps {
    category: Category;
    searchQuery?: string;
    getFilteredItems?: (items: Item[]) => Item[];
    onItemClick?: (item: Item) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, searchQuery = '', getFilteredItems, onItemClick }) => {
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [categorySearchQuery, setCategorySearchQuery] = useState<string>('');

    // Sort items by createdAt descending (newest first)
    const sortItemsByCreatedAt = useCallback((items: Item[]): Item[] => {
        return [...items].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;

            // If both have dates, sort descending (newest first)
            if (dateA > 0 && dateB > 0) {
                return dateB - dateA;
            }

            // Items with dates come before items without dates
            if (dateA > 0 && dateB === 0) return -1;
            if (dateA === 0 && dateB > 0) return 1;

            // If both lack dates, maintain original order
            return 0;
        });
    }, []);

    const fetchItems = useCallback(async () => {
        try {
            setLoading(true);
            const response = await productsApi.getAll(category.id);
            // Backend returns 'products' not 'items'
            const fetchedItems = response.data.products || response.data.items || [];
            setItems(sortItemsByCreatedAt(fetchedItems));
        } catch (err) {
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    }, [category.id, sortItemsByCreatedAt]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    // Apply global search filter if search query exists
    let filteredItems = getFilteredItems ? getFilteredItems(items) : items;

    // Apply category-specific search when expanded
    let finalFilteredItems = filteredItems;
    if (showAll && categorySearchQuery.trim()) {
        const query = categorySearchQuery.toLowerCase().trim();
        finalFilteredItems = filteredItems.filter(item =>
            item.id.toLowerCase().includes(query) ||
            item.name.toLowerCase().includes(query)
        );
        // Ensure filtered items are sorted
        finalFilteredItems = sortItemsByCreatedAt(finalFilteredItems);
    }

    // Show first 6 items initially, all items when showAll is true
    const initialItemsToShow = 6;
    const displayItems = showAll ? finalFilteredItems : filteredItems.slice(0, initialItemsToShow);
    const hasMoreItems = filteredItems.length > 0; // Always show "See More" if there are any items

    const handleShowMore = () => {
        setShowAll(true);
    };

    const handleShowLess = () => {
        setShowAll(false);
        setCategorySearchQuery(''); // Clear category search when collapsing
    };

    return (
        <div className="category-card" id={`category-${category.id}`}>
            <div className="category-header-user">
                <h3 className="category-title">{category.name}</h3>
                {showAll && (
                    <div className="category-search-container-user">
                        <input
                            type="text"
                            className="category-search-input-user"
                            placeholder="Search items in this category..."
                            value={categorySearchQuery}
                            onChange={(e) => setCategorySearchQuery(e.target.value)}
                        />
                        {categorySearchQuery && (
                            <button
                                className="category-search-clear-user"
                                onClick={() => setCategorySearchQuery('')}
                                title="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="loading">
                    <div className="spinner"></div>
                </div>
            ) : finalFilteredItems.length === 0 ? (
                <div className="empty-category">
                    <p>{searchQuery || categorySearchQuery ? 'No items found matching your search.' : 'No items available in this category yet.'}</p>
                </div>
            ) : (
                <>
                    <div className={`items-container ${showAll ? 'show-all' : ''}`}>
                        <div className={`items-scroll ${showAll ? 'items-scroll-expanded' : ''}`}>
                            {displayItems.map((item) => (
                                <ItemCard key={item.id} item={item} onItemClick={onItemClick} />
                            ))}
                            {showAll && finalFilteredItems.length === 0 && (
                                <div className="empty-category-search-user">
                                    <p>No items found matching "{categorySearchQuery}"</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {hasMoreItems && !showAll && (
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button
                                className="btn btn-primary see-more-btn"
                                onClick={handleShowMore}
                            >
                                {filteredItems.length > initialItemsToShow
                                    ? `See More (${filteredItems.length - initialItemsToShow} more)`
                                    : `See All (${filteredItems.length} items)`}
                            </button>
                        </div>
                    )}
                    {showAll && (
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button
                                className="btn btn-secondary see-less-btn"
                                onClick={handleShowLess}
                            >
                                Show Less
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

interface ItemCardProps {
    item: Item;
    onItemClick?: (item: Item) => void;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, onItemClick }) => {
    const { showError, showSuccess } = useToast();
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Get all available images: main, sub-main, and color variations
    // Use useMemo to prevent recalculation on every render
    const allImages = useMemo<Array<{ type: string; url: string; color?: string }>>(() => {
        const images: Array<{ type: string; url: string; color?: string }> = [];

        if (item.mainImage) {
            images.push({ type: 'main', url: item.mainImage });
        }
        if (item.subMainImage) {
            images.push({ type: 'sub-main', url: item.subMainImage });
        }
        if (item.colorVariations && item.colorVariations.length > 0) {
            item.colorVariations.forEach((variation) => {
                if (variation.image) {
                    images.push({ type: 'color', url: variation.image, color: variation.color });
                }
            });
        }
        // Fallback to legacy images array
        if (images.length === 0 && item.images && item.images.length > 0) {
            item.images.forEach((img) => {
                if (img) {
                    images.push({ type: 'legacy', url: img });
                }
            });
        }

        return images;
    }, [item.mainImage, item.subMainImage, item.colorVariations, item.images]);

    // Reset selectedImageIndex if it's out of bounds
    useEffect(() => {
        if (selectedImageIndex >= allImages.length && allImages.length > 0) {
            setSelectedImageIndex(0);
        }
    }, [allImages.length, selectedImageIndex]);

    // Helper to find color variation image index
    const findColorImageIndex = useCallback((colorName: string): number => {
        const colorImageIndex = allImages.findIndex(img =>
            img.type === 'color' && img.color?.toLowerCase() === colorName.toLowerCase()
        );
        return colorImageIndex >= 0 ? colorImageIndex : 0;
    }, [allImages]);

    const currentImage = allImages[selectedImageIndex] || allImages[0];

    return (
        <div
            className="item-card"
            onClick={() => onItemClick && onItemClick(item)}
            style={{ cursor: 'pointer' }}
        >
            {currentImage && (
                <div className="item-image">
                    <img
                        key={`${item.id}-img-${selectedImageIndex}`}
                        src={currentImage.url}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                    />
                    {allImages.length > 1 && (
                        <div className="image-indicators">
                            {allImages.map((img, idx) => (
                                <button
                                    key={`indicator-${idx}-${img.url}`}
                                    className={`indicator ${idx === selectedImageIndex ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedImageIndex(idx);
                                    }}
                                    title={img.color || img.type}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className="item-details">
                <div className="item-id-badge">ID: {item.id}</div>
                <OverflowTooltip
                    text={item.name}
                    tooltipText={getFullProductTitle(item)}
                    className="item-name"
                    as="h4"
                />
                {item.description && (
                    <OverflowTooltip text={item.description} className="item-description" as="p" />
                )}
                {item.colorVariations && item.colorVariations.length > 0 && (
                    <div className="color-variations">
                        <span className="color-label">Available Colors: </span>
                        <div className="color-chips">
                            {item.colorVariations.map((variation, idx) => {
                                const colorImageIndex = findColorImageIndex(variation.color);
                                return (
                                    <span
                                        key={idx}
                                        className={`color-chip ${selectedImageIndex === colorImageIndex ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedImageIndex(colorImageIndex);
                                        }}
                                        title={variation.color}
                                    >
                                        {variation.color}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
                {item.size && (
                    <p className="item-size">Size: {item.size}</p>
                )}
                <div className="item-pricing">
                    {item.mrp && item.mrp > item.price && (
                        <div className="price-discount">
                            <span className="item-mrp">₹{item.mrp}</span>
                            <span className="discount-badge">
                                {cartService.calculateDiscount(item.price, item.mrp)}% OFF
                            </span>
                        </div>
                    )}
                    <p className="item-price">₹{item.price}</p>
                </div>
                {/* Out of Stock Badge */}
                {(item.stock === 0 || item.isOutOfStock) && (
                    <div className="out-of-stock-badge">
                        <span>Out of Stock</span>
                    </div>
                )}
                <div className="item-actions-user">
                    <button
                        className={`btn btn-cart ${(item.stock === 0 || item.isOutOfStock) ? 'disabled' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item.stock === 0 || item.isOutOfStock) {
                                showError('This item is out of stock');
                                return;
                            }
                            const selectedColor = allImages[selectedImageIndex]?.color;
                            cartService.addToCart(item, 1, selectedColor, item.size);
                            showSuccess('Item added to cart!');
                        }}
                        disabled={item.stock === 0 || item.isOutOfStock}
                    >
                        {(item.stock === 0 || item.isOutOfStock) ? 'Out of Stock' : 'Add to Cart'}
                    </button>
                    <button
                        className={`btn btn-buy-now ${(item.stock === 0 || item.isOutOfStock) ? 'disabled' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item.stock === 0 || item.isOutOfStock) {
                                showError('This item is out of stock');
                                return;
                            }
                            const selectedColor = allImages[selectedImageIndex]?.color;
                            cartService.addToCart(item, 1, selectedColor, item.size);
                            window.dispatchEvent(new CustomEvent('openCart', { detail: { checkout: true } }));
                        }}
                        disabled={item.stock === 0 || item.isOutOfStock}
                    >
                        {(item.stock === 0 || item.isOutOfStock) ? 'Out of Stock' : 'Buy Now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface ItemDetailModalProps {
    item: Item;
    onClose: () => void;
    imageIndex: number;
    onImageIndexChange: (index: number) => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ item, onClose, imageIndex, onImageIndexChange }) => {
    const { showError, showSuccess } = useToast();
    // Get all available images: main, sub-main, and color variations
    const allImages: Array<{ type: string; url: string; color?: string }> = [];

    if (item.mainImage) {
        allImages.push({ type: 'main', url: item.mainImage });
    }
    if (item.subMainImage) {
        allImages.push({ type: 'sub-main', url: item.subMainImage });
    }
    if (item.colorVariations && item.colorVariations.length > 0) {
        item.colorVariations.forEach((variation) => {
            allImages.push({ type: 'color', url: variation.image, color: variation.color });
        });
    }
    // Fallback to legacy images array
    if (allImages.length === 0 && item.images && item.images.length > 0) {
        item.images.forEach((img) => {
            allImages.push({ type: 'legacy', url: img });
        });
    }

    const currentImage = allImages[imageIndex] || allImages[0];

    const handlePreviousImage = () => {
        if (imageIndex > 0) {
            onImageIndexChange(imageIndex - 1);
        } else {
            onImageIndexChange(allImages.length - 1);
        }
    };

    const handleNextImage = () => {
        if (imageIndex < allImages.length - 1) {
            onImageIndexChange(imageIndex + 1);
        } else {
            onImageIndexChange(0);
        }
    };

    const findColorImageIndex = (colorName: string): number => {
        const colorImageIndex = allImages.findIndex(img =>
            img.type === 'color' && img.color?.toLowerCase() === colorName.toLowerCase()
        );
        return colorImageIndex >= 0 ? colorImageIndex : 0;
    };

    return (
        <div className="item-modal-overlay" onClick={onClose}>
            <div className="item-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="item-modal-close" onClick={onClose}>×</button>

                <div className="item-modal-body">
                    {/* Image Section */}
                    <div className="item-modal-image-section">
                        {currentImage && (
                            <div className="item-modal-image-container">
                                <img src={currentImage.url} alt={item.name} className="item-modal-main-image" loading="lazy" decoding="async" />
                                {allImages.length > 1 && (
                                    <>
                                        <button
                                            className="item-modal-nav-btn item-modal-nav-prev"
                                            onClick={handlePreviousImage}
                                            aria-label="Previous image"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            className="item-modal-nav-btn item-modal-nav-next"
                                            onClick={handleNextImage}
                                            aria-label="Next image"
                                        >
                                            ›
                                        </button>
                                        <div className="item-modal-image-indicators">
                                            {allImages.map((img, idx) => (
                                                <button
                                                    key={idx}
                                                    className={`item-modal-indicator ${idx === imageIndex ? 'active' : ''}`}
                                                    onClick={() => onImageIndexChange(idx)}
                                                    title={img.color || img.type}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div className="item-modal-details-section">
                        <div className="item-modal-header">
                            <div className="item-modal-id">ID: {item.id}</div>
                            <OverflowTooltip
                                text={item.name}
                                tooltipText={getFullProductTitle(item)}
                                className="item-modal-title"
                                as="h2"
                            />
                        </div>

                        {item.description && (
                            <div className="item-modal-description">
                                <h3>Description</h3>
                                <p>{item.description}</p>
                            </div>
                        )}

                        {item.size && (
                            <div className="item-modal-size">
                                <h3>Size</h3>
                                <p>{item.size}</p>
                            </div>
                        )}

                        {item.colorVariations && item.colorVariations.length > 0 && (
                            <div className="item-modal-colors">
                                <h3>Available Colors</h3>
                                <div className="item-modal-color-chips">
                                    {item.colorVariations.map((variation, idx) => {
                                        const colorImageIndex = findColorImageIndex(variation.color);
                                        return (
                                            <button
                                                key={idx}
                                                className={`item-modal-color-chip ${imageIndex === colorImageIndex ? 'active' : ''}`}
                                                onClick={() => onImageIndexChange(colorImageIndex)}
                                                style={{ backgroundColor: variation.color.toLowerCase() }}
                                                title={variation.color}
                                            >
                                                {variation.color}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="item-modal-pricing">
                            <div className="item-modal-price-section">
                                {item.mrp && item.mrp > item.price && (
                                    <div className="item-modal-mrp-row">
                                        <span className="item-modal-mrp">MRP: ₹{item.mrp}</span>
                                        <span className="item-modal-discount">
                                            {cartService.calculateDiscount(item.price, item.mrp)}% OFF
                                        </span>
                                    </div>
                                )}
                                <div className="item-modal-price">₹{item.price}</div>
                            </div>

                            {(item.stock === 0 || item.isOutOfStock) && (
                                <div className="item-modal-out-of-stock">
                                    <span>Out of Stock</span>
                                </div>
                            )}
                        </div>

                        <div className="item-modal-actions">
                            <button
                                className={`btn btn-modal-cart ${(item.stock === 0 || item.isOutOfStock) ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (item.stock === 0 || item.isOutOfStock) {
                                        showError('This item is out of stock');
                                        return;
                                    }
                                    const selectedColor = allImages[imageIndex]?.color;
                                    cartService.addToCart(item, 1, selectedColor, item.size);
                                    showSuccess('Item added to cart!');
                                }}
                                disabled={item.stock === 0 || item.isOutOfStock}
                            >
                                {(item.stock === 0 || item.isOutOfStock) ? 'Out of Stock' : 'Add to Cart'}
                            </button>
                            <button
                                className={`btn btn-modal-buy ${(item.stock === 0 || item.isOutOfStock) ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (item.stock === 0 || item.isOutOfStock) {
                                        showError('This item is out of stock');
                                        return;
                                    }
                                    const selectedColor = allImages[imageIndex]?.color;
                                    cartService.addToCart(item, 1, selectedColor, item.size);
                                    window.dispatchEvent(new CustomEvent('openCart', { detail: { checkout: true } }));
                                    onClose();
                                }}
                                disabled={item.stock === 0 || item.isOutOfStock}
                            >
                                {(item.stock === 0 || item.isOutOfStock) ? 'Out of Stock' : 'Buy Now'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Categories;
